import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { getRecentlyViewed, clearRecentlyViewed } from "../../services/recentlyViewedService";
import { getProductImg, handleImgError } from "../../utils/imageHelper";
import "../../styles/recently-viewed.css";

const RecentlyViewed = ({ currentProductId = null, title = "Recently Viewed Pieces" }) => {
  const [items, setItems] = useState([]);

  const loadItems = useCallback(() => {
    const all = getRecentlyViewed();
    // Exclude current product if viewing on ProductDetails
    const visible = currentProductId
      ? all.filter((item) => item.id !== currentProductId)
      : all;
    setItems(visible.slice(0, 4));
  }, [currentProductId]);

  useEffect(() => {
    loadItems();

    const handleUpdate = () => {
      loadItems();
    };

    window.addEventListener("recentlyViewedUpdated", handleUpdate);
    return () => {
      window.removeEventListener("recentlyViewedUpdated", handleUpdate);
    };
  }, [loadItems]);

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div className="recently-viewed-section">
      <div className="recently-viewed__header">
        <div className="recently-viewed__title-group">
          <div className="recently-viewed__icon">
            <i className="ri-history-line"></i>
          </div>
          <div>
            <h4 className="recently-viewed__title">{title}</h4>
            <p className="recently-viewed__subtitle">
              Recently browsed by you in the Atelier
            </p>
          </div>
        </div>

        <button
          type="button"
          className="recently-viewed__clear-btn"
          onClick={() => clearRecentlyViewed()}
          title="Clear browsing history"
        >
          <i className="ri-delete-bin-line"></i> Clear History
        </button>
      </div>

      <Row className="g-3">
        <AnimatePresence>
          {items.map((piece) => (
            <Col lg="3" md="6" sm="6" xs="6" key={piece.id}>
              <motion.div
                className="recently-viewed__card"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.25 }}
              >
                <div className="recently-viewed__thumb-wrap">
                  <Link to={`/shop/${piece.id}`} aria-label={piece.productName}>
                    <img
                      src={getProductImg(piece)}
                      onError={(e) => handleImgError(e, piece)}
                      alt={piece.productName}
                      className="recently-viewed__thumb"
                      loading="lazy"
                    />
                  </Link>
                </div>

                <div className="recently-viewed__content">
                  <div className="recently-viewed__meta-row">
                    <span className="recently-viewed__badge">{piece.category}</span>
                    <span className="recently-viewed__rating">
                      <i className="ri-star-fill"></i> {piece.avgRating || 4.8}
                    </span>
                  </div>

                  <h5 className="recently-viewed__name">
                    <Link to={`/shop/${piece.id}`}>{piece.productName}</Link>
                  </h5>

                  <span className="recently-viewed__material">{piece.material}</span>

                  <div className="recently-viewed__bottom">
                    <span className="recently-viewed__price">${piece.price}</span>
                    <Link
                      to={`/shop/${piece.id}`}
                      className="recently-viewed__btn"
                      title="View Details"
                    >
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </motion.div>
            </Col>
          ))}
        </AnimatePresence>
      </Row>
    </div>
  );
};

export default RecentlyViewed;
